import { useEffect, useMemo, useState } from 'react';
import type {
  AgentConfigSnapshot,
  AgentIntegrationBindingConfig,
  MysteryShopperChecklistDimension,
  MysteryShopperConfig,
  MysteryShopperTargetConfig,
} from '@servicesai/api-client';
import {
  GlassButton,
  GlassCard,
  GlassInput,
  GlassSelect,
  GlassTextarea,
  StatusPill,
  useI18n,
} from '@servicesai/ui';

const BROWSERBASE_KEY = 'browserbase-stagehand';
const MANAGED_REFERENCE_RE = /^env:\/\/[A-Za-z_][A-Za-z0-9_]*$/;
const HOSTNAME_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const SLUG_RE = /^[a-z][a-z0-9_-]*$/;
const MANAGED_REFERENCE_SCHEMES = ['env://'] as const;
const BROWSERBASE_CAPABILITIES = [
  'create_session',
  'run_stagehand',
  'read_live_view',
  'read_session_replay',
  'read_session_logs',
];
const ACTIONS = [
  'navigate',
  'fill_form',
  'sandbox_submit',
  'sandbox_payment',
] as const;
const DIMENSIONS: MysteryShopperChecklistDimension[] = [
  'authentication',
  'findability',
  'navigation',
  'content_clarity',
  'form_usability',
  'validation',
  'accessibility',
  'mobile',
  'bilingual',
  'performance',
  'completion',
];

const DIMENSION_CONTENT: Record<
  MysteryShopperChecklistDimension,
  { label: { en: string; ar: string }; instruction: string }
> = {
  authentication: {
    label: { en: 'Authentication is clear and reliable', ar: 'المصادقة واضحة وموثوقة' },
    instruction: 'Verify that sign-in guidance, recovery, errors and session handling are clear and safe.',
  },
  findability: {
    label: { en: 'Service is easy to find', ar: 'يسهل العثور على الخدمة' },
    instruction: 'Verify that citizens can find the service and understand its purpose from the landing experience.',
  },
  navigation: {
    label: { en: 'Journey navigation is clear', ar: 'التنقل في الرحلة واضح' },
    instruction: 'Verify that progress, next steps, back navigation and exits are predictable.',
  },
  content_clarity: {
    label: { en: 'Content is clear and actionable', ar: 'المحتوى واضح وقابل للتنفيذ' },
    instruction: 'Verify that requirements, eligibility, fees and processing expectations use plain language.',
  },
  form_usability: {
    label: { en: 'Forms are easy to complete', ar: 'النماذج سهلة الاستكمال' },
    instruction: 'Verify that labels, input formats, required fields and help reduce completion effort.',
  },
  validation: {
    label: { en: 'Validation helps recovery', ar: 'التحقق يساعد على تصحيح الأخطاء' },
    instruction: 'Verify that validation is timely, specific and explains how to correct each problem.',
  },
  accessibility: {
    label: { en: 'Experience supports accessibility', ar: 'التجربة تدعم إمكانية الوصول' },
    instruction: 'Verify semantic structure, keyboard operation, labels, alternatives and visible focus.',
  },
  mobile: {
    label: { en: 'Mobile journey remains usable', ar: 'رحلة الهاتف المتحرك سهلة الاستخدام' },
    instruction: 'Verify the journey at the configured mobile viewport without clipping or blocked actions.',
  },
  bilingual: {
    label: { en: 'English and Arabic are equivalent', ar: 'الإنجليزية والعربية متكافئتان' },
    instruction: 'Verify equivalent content, controls and completion behavior in English and Arabic.',
  },
  performance: {
    label: { en: 'Pages respond promptly', ar: 'الصفحات تستجيب بسرعة' },
    instruction: 'Verify that loading and interaction delays do not interrupt the service journey.',
  },
  completion: {
    label: { en: 'Completion outcome is unambiguous', ar: 'نتيجة الإنجاز واضحة' },
    instruction: 'Verify that the final outcome, reference and next steps are clearly communicated.',
  },
};

const ACTION_LABELS: Record<(typeof ACTIONS)[number], { en: string; ar: string }> = {
  navigate: { en: 'Navigate', ar: 'التنقل' },
  fill_form: { en: 'Fill forms', ar: 'تعبئة النماذج' },
  sandbox_submit: { en: 'Sandbox submit', ar: 'إرسال تجريبي' },
  sandbox_payment: { en: 'Sandbox payment', ar: 'دفع تجريبي' },
};

const EMPTY_MYSTERY_CONFIG: MysteryShopperConfig = {
  targets: [],
  defaultTargetId: null,
  maxConcurrentSessions: 1,
  retentionDays: 30,
};

function defaultChecklist(): MysteryShopperTargetConfig['checklist'] {
  return DIMENSIONS.map((dimension) => ({
    id: dimension.replaceAll('_', '-'),
    label: { ...DIMENSION_CONTENT[dimension].label },
    dimension,
    instruction: DIMENSION_CONTENT[dimension].instruction,
    weight: 10,
  }));
}

function defaultBrowserbaseBinding(): AgentIntegrationBindingConfig {
  return {
    adapterKey: BROWSERBASE_KEY,
    displayName: 'Browserbase + Stagehand',
    enabled: true,
    capabilities: [...BROWSERBASE_CAPABILITIES],
    scopes: {},
    secretRef: 'env://BROWSERBASE_API_KEY',
    config: {
      region: 'eu-central-1',
      recordSession: true,
      logSession: true,
      maxConcurrency: 1,
      sessionTimeoutSeconds: 1_800,
    },
  };
}

function safeTarget(): MysteryShopperTargetConfig {
  const id = globalThis.crypto.randomUUID();
  return {
    id,
    name: 'New demo service',
    serviceSlug: null,
    startUrl: 'https://demo.example.invalid/',
    loginUrl: null,
    environment: 'demo',
    allowedDomains: ['demo.example.invalid'],
    allowedPaths: ['/'],
    authStrategy: 'none',
    credentialRef: null,
    languages: ['en', 'ar'],
    viewports: [
      { name: 'desktop', width: 1_440, height: 900 },
      { name: 'mobile', width: 390, height: 844 },
    ],
    crawl: {
      maxPages: 25,
      maxDepth: 4,
      maxStepsPerPage: 15,
      timeoutMs: 600_000,
    },
    checklist: defaultChecklist(),
    scenarios: [{
      id: 'browse-service',
      name: { en: 'Browse service', ar: 'تصفح الخدمة' },
      startPath: '/',
      steps: ['Open the service landing page and review the available journey.'],
      allowedActions: ['navigate'],
      fixture: {},
    }],
    sandboxConfirmed: false,
  };
}

function withMysteryConfig(
  config: AgentConfigSnapshot,
  mysteryShopper: MysteryShopperConfig,
): AgentConfigSnapshot {
  return {
    ...config,
    specialization: {
      ...(config.specialization ?? {}),
      mysteryShopper,
    },
  };
}

function ensureBrowserbase(config: AgentConfigSnapshot): AgentConfigSnapshot {
  const index = config.integrations.findIndex((binding) => binding.adapterKey === BROWSERBASE_KEY);
  if (index < 0) {
    return { ...config, integrations: [...config.integrations, defaultBrowserbaseBinding()] };
  }
  const integrations = config.integrations.map((binding, bindingIndex) =>
    bindingIndex === index
      ? {
          ...binding,
          enabled: true,
          secretRef: binding.secretRef ?? 'env://BROWSERBASE_API_KEY',
        }
      : binding);
  return { ...config, integrations };
}

function sandboxBoundaryFingerprint(target: MysteryShopperTargetConfig): string {
  return JSON.stringify({
    startUrl: target.startUrl,
    loginUrl: target.loginUrl,
    environment: target.environment,
    allowedDomains: target.allowedDomains,
    allowedPaths: target.allowedPaths,
    authStrategy: target.authStrategy,
    credentialRef: target.credentialRef,
    scenarios: target.scenarios.map((scenario) => ({
      id: scenario.id,
      startPath: scenario.startPath,
      steps: scenario.steps,
      allowedActions: scenario.allowedActions,
      fixture: scenario.fixture,
    })),
  });
}

function validHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch {
    return false;
  }
}

function targetPathIsAllowed(pathname: string, allowedPaths: string[]): boolean {
  return allowedPaths.some((entry) => {
    const allowed = entry.length > 1 ? entry.replace(/\/+$/u, '') : entry;
    return allowed === '/' || pathname === allowed || pathname.startsWith(`${allowed}/`);
  });
}

function hostname(value: string): string | null {
  if (!validHttpsUrl(value)) return null;
  return new URL(value).hostname.toLowerCase();
}

function hostIsAllowed(host: string, allowedDomains: string[]): boolean {
  return allowedDomains.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));
}

function isManagedReferenceDraft(value: string): boolean {
  if (!value) return true;
  return MANAGED_REFERENCE_SCHEMES.some((scheme) =>
    scheme.startsWith(value)
    || (value.startsWith(scheme) && /^[A-Za-z0-9._/@-]*$/.test(value.slice(scheme.length))));
}

function nextId(prefix: string, current: string[]): string {
  let suffix = current.length + 1;
  while (current.includes(`${prefix}-${suffix}`)) suffix += 1;
  return `${prefix}-${suffix}`;
}

function numberConfig(config: Record<string, unknown>, key: string, fallback: number): number {
  return typeof config[key] === 'number' && Number.isFinite(config[key]) ? config[key] : fallback;
}

function booleanConfig(config: Record<string, unknown>, key: string, fallback: boolean): boolean {
  return typeof config[key] === 'boolean' ? config[key] : fallback;
}

function arabicIssue(issue: string): string {
  const translations: [string, string][] = [
    ['Maximum concurrent sessions must be between 1 and 20.', 'يجب أن يكون الحد الأقصى للجلسات المتزامنة بين 1 و20.'],
    ['Retention must be between 1 and 365 days.', 'يجب أن تكون مدة الاحتفاظ بين يوم واحد و365 يوماً.'],
    ['The default target must reference a configured target.', 'يجب أن يشير الهدف الافتراضي إلى هدف مهيأ.'],
    ['Targets require the Browserbase + Stagehand integration.', 'تتطلب الأهداف تمكين تكامل Browserbase + Stagehand.'],
    ['Browserbase requires a valid managed API-key reference.', 'يتطلب Browserbase مرجعاً مُداراً صالحاً لمفتاح API.'],
    ['Browserbase provider limits are outside the governed range.', 'حدود مزوّد Browserbase خارج النطاق المحكوم.'],
    ['Visual Mystery Shopper targets require session recording.', 'تتطلب أهداف المتسوق الخفي المرئية تسجيل الجلسة.'],
    ['target ID must be unique.', 'يجب أن يكون معرف الهدف فريداً.'],
    ['name is required.', 'الاسم مطلوب.'],
    ['start URL must be HTTPS.', 'يجب أن يستخدم رابط البداية HTTPS.'],
    ['add at least one allowed domain.', 'أضف نطاقاً مسموحاً به واحداً على الأقل.'],
    ['allowed domains must be hostnames without schemes or paths.', 'يجب أن تكون النطاقات أسماء مضيف دون بروتوكولات أو مسارات.'],
    ['start URL host is outside the allowed domains.', 'مضيف رابط البداية خارج النطاقات المسموح بها.'],
    ['start URL path is outside the allowed paths.', 'مسار رابط البداية خارج المسارات المسموح بها.'],
    ['login URL must be HTTPS.', 'يجب أن يستخدم رابط تسجيل الدخول HTTPS.'],
    ['login URL host is outside the allowed domains.', 'مضيف رابط تسجيل الدخول خارج النطاقات المسموح بها.'],
    ['login URL path is outside the allowed paths.', 'مسار رابط تسجيل الدخول خارج المسارات المسموح بها.'],
    ['add at least one allowed path beginning with “/”.', 'أضف مساراً مسموحاً به واحداً على الأقل يبدأ بعلامة “/”.'],
    ['authenticated targets require a managed credential reference.', 'تتطلب الأهداف المصادق عليها مرجع بيانات اعتماد مُداراً.'],
    ['credential must use a managed env reference.', 'يجب أن تستخدم بيانات الاعتماد مرجع env مُداراً.'],
    ['select at least one language.', 'حدد لغة واحدة على الأقل.'],
    ['viewport sizes are outside the supported range.', 'أحجام منفذ العرض خارج النطاق المدعوم.'],
    ['crawl budgets are outside the governed range.', 'ميزانيات الاستكشاف خارج النطاق المحكوم.'],
    ['add at least one checklist item.', 'أضف بند فحص واحداً على الأقل.'],
    ['checklist IDs must be unique lowercase identifiers.', 'يجب أن تكون معرفات الفحص فريدة وبأحرف لاتينية صغيرة.'],
    ['each checklist dimension may be configured once.', 'يمكن تهيئة كل بُعد من أبعاد قائمة الفحص مرة واحدة.'],
    ['every checklist item needs both labels and an instruction.', 'يحتاج كل بند فحص إلى التسميتين وتعليمات.'],
    ['checklist weights must be between 1 and 100.', 'يجب أن تكون أوزان الفحص بين 1 و100.'],
    ['scenario IDs must be unique lowercase identifiers.', 'يجب أن تكون معرفات السيناريو فريدة وبأحرف لاتينية صغيرة.'],
    ['every scenario needs an English and Arabic name.', 'يحتاج كل سيناريو إلى اسم بالإنجليزية والعربية.'],
    ['scenarios need a rooted start path, steps and at least one allowed action.', 'تحتاج السيناريوهات إلى مسار بداية وخطوات وإجراء مسموح به واحد على الأقل.'],
    ['scenario start path is outside the allowed paths.', 'مسار بداية السيناريو خارج المسارات المسموح بها.'],
    ['fixture keys cannot be empty.', 'لا يمكن أن تكون مفاتيح بيانات الاختبار فارغة.'],
    ['fixture secrets must use the managed credential reference.', 'يجب أن تستخدم أسرار بيانات الاختبار مرجع بيانات الاعتماد المُدار.'],
    ['explicitly confirm the sandbox before enabling submit or payment.', 'أكّد البيئة التجريبية صراحةً قبل تمكين الإرسال أو الدفع.'],
  ];
  for (const [english, arabic] of translations) {
    if (issue === english) return arabic;
    if (issue.endsWith(english)) return `${issue.slice(0, -english.length)}${arabic}`;
  }
  return issue;
}

export function mysteryShopperEditorIssues(config: AgentConfigSnapshot): string[] {
  const mystery = config.specialization?.mysteryShopper;
  if (!mystery) return [];
  const issues: string[] = [];
  if (!Number.isInteger(mystery.maxConcurrentSessions) || mystery.maxConcurrentSessions < 1 || mystery.maxConcurrentSessions > 20) {
    issues.push('Maximum concurrent sessions must be between 1 and 20.');
  }
  if (!Number.isInteger(mystery.retentionDays) || mystery.retentionDays < 1 || mystery.retentionDays > 365) {
    issues.push('Retention must be between 1 and 365 days.');
  }
  if (mystery.defaultTargetId && !mystery.targets.some((target) => target.id === mystery.defaultTargetId)) {
    issues.push('The default target must reference a configured target.');
  }
  const targetIds = new Set<string>();
  for (const [index, target] of mystery.targets.entries()) {
    const label = target.name.trim() || `Target ${index + 1}`;
    if (targetIds.has(target.id)) issues.push(`${label}: target ID must be unique.`);
    targetIds.add(target.id);
    if (!target.name.trim()) issues.push(`Target ${index + 1}: name is required.`);
    const startHost = hostname(target.startUrl);
    if (!startHost) issues.push(`${label}: start URL must be HTTPS.`);
    if (target.allowedDomains.length === 0) issues.push(`${label}: add at least one allowed domain.`);
    if (target.allowedDomains.some((domain) => !HOSTNAME_RE.test(domain))) {
      issues.push(`${label}: allowed domains must be hostnames without schemes or paths.`);
    }
    if (startHost && !hostIsAllowed(startHost, target.allowedDomains)) {
      issues.push(`${label}: start URL host is outside the allowed domains.`);
    }
    if (
      startHost
      && !targetPathIsAllowed(new URL(target.startUrl).pathname, target.allowedPaths)
    ) {
      issues.push(`${label}: start URL path is outside the allowed paths.`);
    }
    const loginHost = target.loginUrl ? hostname(target.loginUrl) : null;
    if (target.loginUrl && !loginHost) issues.push(`${label}: login URL must be HTTPS.`);
    if (loginHost && !hostIsAllowed(loginHost, target.allowedDomains)) {
      issues.push(`${label}: login URL host is outside the allowed domains.`);
    }
    if (
      loginHost
      && !targetPathIsAllowed(new URL(target.loginUrl!).pathname, target.allowedPaths)
    ) {
      issues.push(`${label}: login URL path is outside the allowed paths.`);
    }
    if (target.allowedPaths.length === 0 || target.allowedPaths.some((path) => !path.startsWith('/'))) {
      issues.push(`${label}: add at least one allowed path beginning with “/”.`);
    }
    if (target.authStrategy !== 'none' && !target.credentialRef) {
      issues.push(`${label}: authenticated targets require a managed credential reference.`);
    }
    if (target.credentialRef && !MANAGED_REFERENCE_RE.test(target.credentialRef)) {
      issues.push(`${label}: credential must use a managed env reference.`);
    }
    if (target.languages.length === 0) issues.push(`${label}: select at least one language.`);
    if (
      target.viewports.length === 0
      || target.viewports.some((viewport) =>
        !viewport.name.trim()
        || !Number.isInteger(viewport.width)
        || viewport.width < 320
        || viewport.width > 3_840
        || !Number.isInteger(viewport.height)
        || viewport.height < 480
        || viewport.height > 2_160)
    ) {
      issues.push(`${label}: viewport sizes are outside the supported range.`);
    }
    if (
      !Number.isInteger(target.crawl.maxPages)
      || target.crawl.maxPages < 1
      || target.crawl.maxPages > 500
      || !Number.isInteger(target.crawl.maxDepth)
      || target.crawl.maxDepth < 0
      || target.crawl.maxDepth > 20
      || !Number.isInteger(target.crawl.maxStepsPerPage)
      || target.crawl.maxStepsPerPage < 1
      || target.crawl.maxStepsPerPage > 100
      || !Number.isInteger(target.crawl.timeoutMs)
      || target.crawl.timeoutMs < 60_000
      || target.crawl.timeoutMs > 21_600_000
    ) {
      issues.push(`${label}: crawl budgets are outside the governed range.`);
    }
    if (target.checklist.length === 0) issues.push(`${label}: add at least one checklist item.`);
    const checklistIds = new Set<string>();
    const checklistDimensions = new Set<MysteryShopperChecklistDimension>();
    for (const item of target.checklist) {
      if (!SLUG_RE.test(item.id) || checklistIds.has(item.id)) {
        issues.push(`${label}: checklist IDs must be unique lowercase identifiers.`);
        break;
      }
      checklistIds.add(item.id);
      if (checklistDimensions.has(item.dimension)) {
        issues.push(`${label}: each checklist dimension may be configured once.`);
        break;
      }
      checklistDimensions.add(item.dimension);
      if (!item.label.en.trim() || !item.label.ar.trim() || !item.instruction.trim()) {
        issues.push(`${label}: every checklist item needs both labels and an instruction.`);
        break;
      }
      if (!Number.isInteger(item.weight) || item.weight < 1 || item.weight > 100) {
        issues.push(`${label}: checklist weights must be between 1 and 100.`);
        break;
      }
    }
    const scenarioIds = new Set<string>();
    for (const scenario of target.scenarios) {
      if (!SLUG_RE.test(scenario.id) || scenarioIds.has(scenario.id)) {
        issues.push(`${label}: scenario IDs must be unique lowercase identifiers.`);
        break;
      }
      scenarioIds.add(scenario.id);
      if (!scenario.name.en.trim() || !scenario.name.ar.trim()) {
        issues.push(`${label}: every scenario needs an English and Arabic name.`);
        break;
      }
      if (
        !scenario.startPath.startsWith('/')
        || scenario.steps.length === 0
        || scenario.steps.some((step) => !step.trim())
        || scenario.allowedActions.length === 0
      ) {
        issues.push(`${label}: scenarios need a rooted start path, steps and at least one allowed action.`);
        break;
      }
      if (!targetPathIsAllowed(scenario.startPath, target.allowedPaths)) {
        issues.push(`${label}: scenario start path is outside the allowed paths.`);
        break;
      }
      if (Object.keys(scenario.fixture).some((key) => !key.trim())) {
        issues.push(`${label}: fixture keys cannot be empty.`);
        break;
      }
      if (Object.keys(scenario.fixture).some((key) =>
        /(?:secret|password|token|credential|api[_-]?key|private[_-]?key)/iu.test(key))) {
        issues.push(`${label}: fixture secrets must use the managed credential reference.`);
        break;
      }
    }
    const hasSandboxAction = target.scenarios.some((scenario) =>
      scenario.allowedActions.includes('sandbox_submit')
      || scenario.allowedActions.includes('sandbox_payment'));
    if (hasSandboxAction && !target.sandboxConfirmed) {
      issues.push(`${label}: explicitly confirm the sandbox before enabling submit or payment.`);
    }
  }
  const browserbase = config.integrations.find((binding) => binding.adapterKey === BROWSERBASE_KEY);
  if (mystery.targets.length > 0 && !browserbase?.enabled) {
    issues.push('Targets require the Browserbase + Stagehand integration.');
  }
  if (browserbase && (!browserbase.secretRef || !MANAGED_REFERENCE_RE.test(browserbase.secretRef))) {
    issues.push('Browserbase requires a valid managed API-key reference.');
  }
  if (browserbase) {
    const maxConcurrency = numberConfig(browserbase.config, 'maxConcurrency', 1);
    const sessionTimeoutSeconds = numberConfig(browserbase.config, 'sessionTimeoutSeconds', 1_800);
    if (
      !Number.isInteger(maxConcurrency)
      || maxConcurrency < 1
      || maxConcurrency > 20
      || !Number.isInteger(sessionTimeoutSeconds)
      || sessionTimeoutSeconds < 60
      || sessionTimeoutSeconds > 21_600
    ) {
      issues.push('Browserbase provider limits are outside the governed range.');
    }
    if (browserbase.config.recordSession === false) {
      issues.push('Visual Mystery Shopper targets require session recording.');
    }
  }
  return issues;
}

function NumberField({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [editing, value]);
  return (
    <label className="block text-xs font-bold text-ink-soft">
      {label}
      <GlassInput
        type="number"
        value={draft}
        min={min}
        max={max}
        step={step}
        onFocus={() => setEditing(true)}
        onChange={(event) => {
          setDraft(event.target.value);
          if (event.target.value.trim() === '') return;
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
        onBlur={() => {
          setEditing(false);
          if (draft.trim() === '') setDraft(String(value));
        }}
        className="mt-1"
      />
    </label>
  );
}

function CheckboxField({
  checked,
  label,
  hint,
  onChange,
  tone = 'default',
}: {
  checked: boolean;
  label: string;
  hint?: string;
  onChange: (checked: boolean) => void;
  tone?: 'default' | 'warning';
}) {
  return (
    <label className={[
      'flex min-h-12 cursor-pointer items-start gap-2 rounded-lg border p-3 text-xs',
      tone === 'warning' ? 'border-aegold-300 bg-aegold-50' : 'border-aeblack-100 bg-white',
    ].join(' ')}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0"
      />
      <span>
        <span className="block font-bold">{label}</span>
        {hint && <span className="mt-0.5 block text-[10px] leading-4 text-ink-faint">{hint}</span>}
      </span>
    </label>
  );
}

function ManagedReferenceField({
  label,
  value,
  placeholder,
  onChange,
  help,
}: {
  label: string;
  value: string | null;
  placeholder: string;
  onChange: (value: string | null) => void;
  help: string;
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const [rejected, setRejected] = useState(false);
  const current = value ?? '';
  const unsafeExisting = Boolean(current && !isManagedReferenceDraft(current));
  const complete = !current || MANAGED_REFERENCE_RE.test(current);
  const hintId = `managed-reference-${useMemo(() => crypto.randomUUID(), [])}`;
  return (
    <label className="block text-xs font-bold text-ink-soft">
      {label}
      <GlassInput
        value={unsafeExisting ? '' : current}
        onChange={(event) => {
          const next = event.target.value.trim();
          if (!isManagedReferenceDraft(next)) {
            setRejected(true);
            return;
          }
          setRejected(false);
          onChange(next || null);
        }}
        placeholder={placeholder}
        className="mt-1 font-mono"
        dir="ltr"
        autoComplete="off"
        spellCheck={false}
        aria-invalid={unsafeExisting || rejected || !complete || undefined}
        aria-describedby={hintId}
      />
      <span
        id={hintId}
        className={[
          'mt-1 block text-[10px] font-normal leading-4',
          unsafeExisting || rejected || !complete ? 'text-reject' : 'text-ink-faint',
        ].join(' ')}
      >
        {unsafeExisting
          ? text(
              'The stored value is not a managed reference and is hidden. Replace it before saving.',
              'القيمة المحفوظة ليست مرجعاً مُداراً ولذلك تم إخفاؤها. استبدلها قبل الحفظ.',
            )
          : rejected
            ? text(
                'Raw values are rejected. Enter a supported managed reference.',
                'تُرفض القيم الخام. أدخل مرجعاً مُداراً مدعوماً.',
              )
            : !complete
              ? text(
                  'Finish the managed reference before saving.',
                  'أكمل المرجع المُدار قبل الحفظ.',
                )
              : help}
      </span>
    </label>
  );
}

function SectionHeader({
  title,
  hint,
  action,
}: {
  title: string;
  hint: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <h4 className="text-sm font-extrabold">{title}</h4>
        <p className="mt-1 max-w-3xl text-[11px] leading-4 text-ink-faint">{hint}</p>
      </div>
      {action}
    </div>
  );
}

export function MysteryShopperTargetEditor({
  config,
  onChange,
}: {
  config: AgentConfigSnapshot;
  onChange: (config: AgentConfigSnapshot) => void;
}) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const mystery = config.specialization?.mysteryShopper ?? EMPTY_MYSTERY_CONFIG;
  const [selectedTargetId, setSelectedTargetId] = useState(
    mystery.defaultTargetId ?? mystery.targets[0]?.id ?? '',
  );
  const selectedIndex = mystery.targets.findIndex((target) => target.id === selectedTargetId);
  const selectedTarget = mystery.targets[selectedIndex] ?? mystery.targets[0] ?? null;
  const effectiveSelectedIndex = selectedIndex >= 0 ? selectedIndex : selectedTarget ? 0 : -1;
  const browserbase = config.integrations.find((binding) => binding.adapterKey === BROWSERBASE_KEY);
  const browserbaseConfig = browserbase?.config ?? {};
  const issues = mysteryShopperEditorIssues(config);

  useEffect(() => {
    if (mystery.targets.length === 0) {
      setSelectedTargetId('');
      return;
    }
    if (!mystery.targets.some((target) => target.id === selectedTargetId)) {
      setSelectedTargetId(mystery.defaultTargetId ?? mystery.targets[0]!.id);
    }
  }, [mystery.defaultTargetId, mystery.targets, selectedTargetId]);

  const setMystery = (next: MysteryShopperConfig) => {
    onChange(withMysteryConfig(config, next));
  };
  const updateTarget = (next: MysteryShopperTargetConfig) => {
    if (effectiveSelectedIndex < 0) return;
    const current = mystery.targets[effectiveSelectedIndex]!;
    const boundaryChanged =
      sandboxBoundaryFingerprint(current) !== sandboxBoundaryFingerprint(next);
    const guarded = boundaryChanged && next.sandboxConfirmed
      ? { ...next, sandboxConfirmed: false }
      : next;
    setMystery({
      ...mystery,
      targets: mystery.targets.map((target, index) =>
        index === effectiveSelectedIndex ? guarded : target),
    });
  };
  const updateBrowserbase = (
    transform: (binding: AgentIntegrationBindingConfig) => AgentIntegrationBindingConfig,
  ) => {
    const index = config.integrations.findIndex((binding) => binding.adapterKey === BROWSERBASE_KEY);
    const base = index >= 0 ? config.integrations[index]! : defaultBrowserbaseBinding();
    const next = transform(base);
    onChange({
      ...config,
      integrations: index >= 0
        ? config.integrations.map((binding, bindingIndex) => bindingIndex === index ? next : binding)
        : [...config.integrations, next],
    });
  };
  const addTarget = () => {
    const target = safeTarget();
    const first = mystery.targets.length === 0;
    const nextMystery = {
      ...mystery,
      targets: [...mystery.targets, target],
      defaultTargetId: mystery.defaultTargetId ?? target.id,
    };
    const nextConfig = withMysteryConfig(config, nextMystery);
    onChange(first ? ensureBrowserbase(nextConfig) : nextConfig);
    setSelectedTargetId(target.id);
  };
  const removeSelectedTarget = () => {
    if (!selectedTarget) return;
    const targets = mystery.targets.filter((target) => target.id !== selectedTarget.id);
    const defaultTargetId = mystery.defaultTargetId === selectedTarget.id
      ? targets[0]?.id ?? null
      : mystery.defaultTargetId;
    setMystery({ ...mystery, targets, defaultTargetId });
    setSelectedTargetId(targets[0]?.id ?? '');
  };
  const updateViewport = (name: 'desktop' | 'mobile', key: 'width' | 'height', value: number) => {
    if (!selectedTarget) return;
    const index = selectedTarget.viewports.findIndex((viewport) => viewport.name.toLowerCase() === name);
    const fallback = name === 'desktop'
      ? { name: 'desktop', width: 1_440, height: 900 }
      : { name: 'mobile', width: 390, height: 844 };
    const viewports = index < 0
      ? [...selectedTarget.viewports, { ...fallback, [key]: value }]
      : selectedTarget.viewports.map((viewport, viewportIndex) =>
          viewportIndex === index ? { ...viewport, [key]: value } : viewport);
    updateTarget({ ...selectedTarget, viewports });
  };
  const viewport = (name: 'desktop' | 'mobile') =>
    selectedTarget?.viewports.find((item) => item.name.toLowerCase() === name)
    ?? (name === 'desktop'
      ? { name, width: 1_440, height: 900 }
      : { name, width: 390, height: 844 });

  return (
    <GlassCard className="overflow-hidden p-0">
      <div className="border-b border-aeblack-100 bg-gradient-to-r from-seablue-50 via-white to-aegold-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-extrabold">
                {text('Mystery Shopper targets', 'أهداف المتسوق الخفي')}
              </h3>
              <StatusPill
                state={issues.length === 0 ? 'published' : 'in_review'}
                label={issues.length === 0
                  ? text('Ready for validation', 'جاهز للتحقق')
                  : text(`${issues.length} setup checks`, `${issues.length} متطلبات إعداد`)}
              />
            </div>
            <p className="mt-1 max-w-3xl text-xs leading-relaxed text-ink-faint">
              {text(
                'Define allowlisted demo journeys and managed credentials in this draft before maker-checker publication.',
                'حدّد رحلات العرض المسموح بها ومراجع بيانات الاعتماد المُدارة في هذه المسودة قبل النشر باعتماد الصانع والمدقق.',
              )}
            </p>
          </div>
          <GlassButton type="button" variant="primary" onClick={addTarget}>
            {text('Add target', 'إضافة هدف')}
          </GlassButton>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <NumberField
            label={text('Maximum concurrent sessions', 'الحد الأقصى للجلسات المتزامنة')}
            value={mystery.maxConcurrentSessions}
            min={1}
            max={20}
            onChange={(value) => setMystery({ ...mystery, maxConcurrentSessions: value })}
          />
          <NumberField
            label={text('Evidence retention (days)', 'الاحتفاظ بالأدلة (أيام)')}
            value={mystery.retentionDays}
            min={1}
            max={365}
            onChange={(value) => setMystery({ ...mystery, retentionDays: value })}
          />
          <label className="block text-xs font-bold text-ink-soft">
            {text('Default target', 'الهدف الافتراضي')}
            <GlassSelect
              value={mystery.defaultTargetId ?? ''}
              onChange={(event) => setMystery({
                ...mystery,
                defaultTargetId: event.target.value || null,
              })}
              className="mt-1"
            >
              <option value="">{text('Not set', 'غير محدد')}</option>
              {mystery.targets.map((target) => (
                <option key={target.id} value={target.id}>{target.name || target.id}</option>
              ))}
            </GlassSelect>
          </label>
        </div>
      </div>

      <div className="grid min-h-72 lg:grid-cols-[250px_minmax(0,1fr)]">
        <aside className="border-b border-aeblack-100 bg-aeblack-50 p-3 lg:border-e lg:border-b-0">
          <div className="mb-2 px-2 text-[10px] font-extrabold uppercase tracking-wider text-ink-faint">
            {text(`${mystery.targets.length} configured targets`, `${mystery.targets.length} أهداف مهيأة`)}
          </div>
          <div className="space-y-1">
            {mystery.targets.map((target) => {
              const active = selectedTarget?.id === target.id;
              return (
                <button
                  key={target.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelectedTargetId(target.id)}
                  className={[
                    'w-full rounded-lg border p-3 text-start transition',
                    active
                      ? 'border-seablue-300 bg-white shadow-sm ring-1 ring-seablue-100'
                      : 'border-transparent hover:border-aeblack-100 hover:bg-white',
                  ].join(' ')}
                >
                  <span className="block truncate text-xs font-extrabold">{target.name || text('Unnamed target', 'هدف بلا اسم')}</span>
                  <span className="mt-1 block truncate font-mono text-[9px] text-ink-faint">{hostname(target.startUrl) ?? target.startUrl}</span>
                  <span className="mt-2 inline-flex rounded-full bg-aeblack-100 px-2 py-0.5 text-[9px] font-bold uppercase text-ink-soft">
                    {target.environment}
                  </span>
                </button>
              );
            })}
          </div>
          {mystery.targets.length === 0 && (
            <div className="rounded-lg border border-dashed border-aeblack-200 bg-white p-4 text-center">
              <div className="text-2xl" aria-hidden="true">◎</div>
              <p className="mt-2 text-xs font-bold">{text('No targets yet', 'لا توجد أهداف بعد')}</p>
              <p className="mt-1 text-[10px] leading-4 text-ink-faint">
                {text('The first target starts read-only with safe MOEI defaults.', 'يبدأ الهدف الأول للقراءة فقط بإعدادات الوزارة الآمنة.')}
              </p>
            </div>
          )}
        </aside>

        <div className="min-w-0 space-y-5 p-5">
          {selectedTarget ? (
            <>
              <section>
                <SectionHeader
                  title={text('Service boundary', 'نطاق الخدمة')}
                  hint={text(
                    'Only HTTPS hosts and paths listed here can be visited. Changing a URL never broadens the allowlist.',
                    'لا يمكن زيارة سوى مضيفي ومسارات HTTPS المدرجة هنا. تغيير الرابط لا يوسّع قائمة السماح.',
                  )}
                  action={(
                    <GlassButton type="button" size="sm" variant="danger" onClick={removeSelectedTarget}>
                      {text('Remove target', 'إزالة الهدف')}
                    </GlassButton>
                  )}
                />
                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                  <label className="block text-xs font-bold text-ink-soft">
                    {text('Target name', 'اسم الهدف')}
                    <GlassInput
                      value={selectedTarget.name}
                      onChange={(event) => updateTarget({ ...selectedTarget, name: event.target.value })}
                      className="mt-1"
                    />
                  </label>
                  <label className="block text-xs font-bold text-ink-soft">
                    {text('Environment', 'البيئة')}
                    <GlassSelect
                      value={selectedTarget.environment}
                      onChange={(event) => updateTarget({
                        ...selectedTarget,
                        environment: event.target.value as MysteryShopperTargetConfig['environment'],
                      })}
                      className="mt-1"
                    >
                      <option value="demo">{text('Demo', 'عرض تجريبي')}</option>
                      <option value="staging">{text('Staging', 'مرحلة تجريبية')}</option>
                    </GlassSelect>
                  </label>
                  <label className="block text-xs font-bold text-ink-soft lg:col-span-2">
                    {text('Start URL', 'رابط البداية')}
                    <GlassInput
                      value={selectedTarget.startUrl}
                      type="url"
                      inputMode="url"
                      dir="ltr"
                      onChange={(event) => updateTarget({ ...selectedTarget, startUrl: event.target.value })}
                      onBlur={(event) => {
                        if (
                          selectedTarget.allowedDomains.some((domain) =>
                            domain.trim() !== '')
                        ) return;
                        const derived = hostname(event.target.value);
                        if (derived) updateTarget({ ...selectedTarget, startUrl: event.target.value, allowedDomains: [derived] });
                      }}
                      aria-invalid={!validHttpsUrl(selectedTarget.startUrl) || undefined}
                      className="mt-1 font-mono"
                    />
                    <span className="mt-1 block text-[10px] font-normal text-ink-faint">
                      {text(
                        'HTTPS only. On blur, its exact hostname is added only when the domain list is empty.',
                        'HTTPS فقط. عند مغادرة الحقل، يضاف اسم المضيف نفسه فقط إذا كانت قائمة النطاقات فارغة.',
                      )}
                    </span>
                  </label>
                  <label className="block text-xs font-bold text-ink-soft">
                    {text('Allowed domains — one per line', 'النطاقات المسموح بها — واحد في كل سطر')}
                    <GlassTextarea
                      value={selectedTarget.allowedDomains.join('\n')}
                      rows={4}
                      dir="ltr"
                      onChange={(event) => updateTarget({
                        ...selectedTarget,
                        allowedDomains: event.target.value
                          .split('\n')
                          .map((value) => value.trim().toLowerCase()),
                      })}
                      placeholder="demo.example.gov.ae"
                      className="mt-1 font-mono"
                    />
                    <span className="mt-1 block text-[10px] font-normal text-ink-faint">
                      {text('Hostnames only; no schemes, wildcards or paths.', 'أسماء المضيف فقط؛ دون بروتوكولات أو أحرف بدل أو مسارات.')}
                    </span>
                  </label>
                  <label className="block text-xs font-bold text-ink-soft">
                    {text('Allowed paths — one per line', 'المسارات المسموح بها — واحد في كل سطر')}
                    <GlassTextarea
                      value={selectedTarget.allowedPaths.join('\n')}
                      rows={4}
                      dir="ltr"
                      onChange={(event) => updateTarget({
                        ...selectedTarget,
                        allowedPaths: event.target.value
                          .split('\n')
                          .map((value) => value.trim()),
                      })}
                      placeholder="/services"
                      className="mt-1 font-mono"
                    />
                    <span className="mt-1 block text-[10px] font-normal text-ink-faint">
                      {text('Every path must begin with “/”.', 'يجب أن يبدأ كل مسار بعلامة “/”.')}
                    </span>
                  </label>
                </div>
              </section>

              <section className="border-t border-aeblack-100 pt-5">
                <SectionHeader
                  title={text('Demo login and experience', 'تسجيل الدخول وتجربة العرض')}
                  hint={text(
                    'Credentials remain in the managed secret store; this draft contains references only.',
                    'تبقى بيانات الاعتماد في مخزن الأسرار المُدار؛ تحتوي هذه المسودة على المراجع فقط.',
                  )}
                />
                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                  <label className="block text-xs font-bold text-ink-soft">
                    {text('Optional login URL', 'رابط تسجيل الدخول الاختياري')}
                    <GlassInput
                      value={selectedTarget.loginUrl ?? ''}
                      type="url"
                      inputMode="url"
                      dir="ltr"
                      onChange={(event) => updateTarget({
                        ...selectedTarget,
                        loginUrl: event.target.value || null,
                      })}
                      aria-invalid={Boolean(selectedTarget.loginUrl && !validHttpsUrl(selectedTarget.loginUrl)) || undefined}
                      placeholder="https://demo.example.gov.ae/login"
                      className="mt-1 font-mono"
                    />
                  </label>
                  <label className="block text-xs font-bold text-ink-soft">
                    {text('Authentication strategy', 'استراتيجية المصادقة')}
                    <GlassSelect
                      value={selectedTarget.authStrategy}
                      onChange={(event) => updateTarget({
                        ...selectedTarget,
                        authStrategy: event.target.value as MysteryShopperTargetConfig['authStrategy'],
                      })}
                      className="mt-1"
                    >
                      <option value="none">{text('No login', 'دون تسجيل دخول')}</option>
                      <option value="username_password">{text('Managed username and password', 'اسم مستخدم وكلمة مرور مُداران')}</option>
                    </GlassSelect>
                  </label>
                  {selectedTarget.authStrategy !== 'none' && (
                    <div className="lg:col-span-2">
                      <ManagedReferenceField
                        label={text('Demo credential reference', 'مرجع بيانات اعتماد العرض')}
                        value={selectedTarget.credentialRef}
                        onChange={(credentialRef) => updateTarget({ ...selectedTarget, credentialRef })}
                        placeholder="env://MYSTERY_SHOP_DEMO_CREDENTIALS"
                        help={text(
                          'Accepted in this deployment: env://. Raw usernames and passwords are rejected.',
                          'المقبول في هذا النشر: env://. تُرفض أسماء المستخدمين وكلمات المرور الخام.',
                        )}
                      />
                    </div>
                  )}
                  <div>
                    <div className="mb-1 text-xs font-bold text-ink-soft">{text('Languages', 'اللغات')}</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(['en', 'ar'] as const).map((language) => (
                        <CheckboxField
                          key={language}
                          checked={selectedTarget.languages.includes(language)}
                          label={language === 'en' ? text('English', 'الإنجليزية') : text('Arabic / RTL', 'العربية / من اليمين إلى اليسار')}
                          onChange={(checked) => updateTarget({
                            ...selectedTarget,
                            languages: checked
                              ? [...selectedTarget.languages, language]
                              : selectedTarget.languages.filter((item) => item !== language),
                          })}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-bold text-ink-soft">{text('Viewport defaults', 'إعدادات منفذ العرض')}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <NumberField label={text('Desktop width', 'عرض سطح المكتب')} value={viewport('desktop').width} min={320} max={3_840} onChange={(value) => updateViewport('desktop', 'width', value)} />
                      <NumberField label={text('Desktop height', 'ارتفاع سطح المكتب')} value={viewport('desktop').height} min={480} max={2_160} onChange={(value) => updateViewport('desktop', 'height', value)} />
                      <NumberField label={text('Mobile width', 'عرض الهاتف')} value={viewport('mobile').width} min={320} max={3_840} onChange={(value) => updateViewport('mobile', 'width', value)} />
                      <NumberField label={text('Mobile height', 'ارتفاع الهاتف')} value={viewport('mobile').height} min={480} max={2_160} onChange={(value) => updateViewport('mobile', 'height', value)} />
                    </div>
                  </div>
                </div>
              </section>

              <section className="border-t border-aeblack-100 pt-5">
                <SectionHeader
                  title={text('Crawl budgets', 'ميزانيات الاستكشاف')}
                  hint={text(
                    'Hard limits stop sessions from exploring beyond the approved time and depth.',
                    'توقف الحدود الصارمة الجلسات عن الاستكشاف خارج الوقت والعمق المعتمدين.',
                  )}
                />
                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <NumberField label={text('Maximum pages', 'الحد الأقصى للصفحات')} value={selectedTarget.crawl.maxPages} min={1} max={500} onChange={(value) => updateTarget({ ...selectedTarget, crawl: { ...selectedTarget.crawl, maxPages: value } })} />
                  <NumberField label={text('Maximum depth', 'الحد الأقصى للعمق')} value={selectedTarget.crawl.maxDepth} min={0} max={20} onChange={(value) => updateTarget({ ...selectedTarget, crawl: { ...selectedTarget.crawl, maxDepth: value } })} />
                  <NumberField label={text('Steps per page', 'الخطوات لكل صفحة')} value={selectedTarget.crawl.maxStepsPerPage} min={1} max={100} onChange={(value) => updateTarget({ ...selectedTarget, crawl: { ...selectedTarget.crawl, maxStepsPerPage: value } })} />
                  <NumberField label={text('Timeout (milliseconds)', 'المهلة (مللي ثانية)')} value={selectedTarget.crawl.timeoutMs} min={60_000} max={21_600_000} step={30_000} onChange={(value) => updateTarget({ ...selectedTarget, crawl: { ...selectedTarget.crawl, timeoutMs: value } })} />
                </div>
              </section>

              <section className="border-t border-aeblack-100 pt-5">
                <SectionHeader
                  title={text('Service-quality checklist', 'قائمة فحص جودة الخدمة')}
                  hint={text(
                    'Weighted, bilingual checks define how evidence is assessed and scored.',
                    'تحدد الفحوص الموزونة وثنائية اللغة كيفية تقييم الأدلة واحتساب الدرجات.',
                  )}
                  action={(
                    <div className="flex flex-wrap gap-2">
                      <GlassButton
                        type="button"
                        size="sm"
                        onClick={() => updateTarget({ ...selectedTarget, checklist: defaultChecklist() })}
                      >
                        {text('Use MOEI defaults', 'استخدام إعدادات الوزارة')}
                      </GlassButton>
                      <GlassButton
                        type="button"
                        size="sm"
                        onClick={() => updateTarget({
                          ...selectedTarget,
                          checklist: [...selectedTarget.checklist, {
                            id: nextId('check', selectedTarget.checklist.map((item) => item.id)),
                            label: { en: '', ar: '' },
                            dimension: 'navigation',
                            instruction: '',
                            weight: 10,
                          }],
                        })}
                      >
                        {text('Add check', 'إضافة فحص')}
                      </GlassButton>
                    </div>
                  )}
                />
                <div className="mt-3 space-y-3">
                  {selectedTarget.checklist.map((item, itemIndex) => (
                    <div key={`checklist-row-${itemIndex}`} className="rounded-xl border border-aeblack-100 bg-aeblack-50 p-3">
                      <div className="grid gap-2 lg:grid-cols-[minmax(120px,.7fr)_minmax(160px,1fr)_100px_auto]">
                        <label className="text-[10px] font-bold text-ink-faint">
                          {text('Check ID', 'معرف الفحص')}
                          <GlassInput
                            value={item.id}
                            dir="ltr"
                            onChange={(event) => updateTarget({
                              ...selectedTarget,
                              checklist: selectedTarget.checklist.map((current, index) =>
                                index === itemIndex ? { ...current, id: event.target.value } : current),
                            })}
                            className="mt-1 font-mono"
                          />
                        </label>
                        <label className="text-[10px] font-bold text-ink-faint">
                          {text('Dimension', 'البُعد')}
                          <GlassSelect
                            value={item.dimension}
                            onChange={(event) => updateTarget({
                              ...selectedTarget,
                              checklist: selectedTarget.checklist.map((current, index) =>
                                index === itemIndex
                                  ? { ...current, dimension: event.target.value as MysteryShopperChecklistDimension }
                                  : current),
                            })}
                            className="mt-1"
                          >
                            {DIMENSIONS.map((dimension) => (
                              <option key={dimension} value={dimension}>{dimension.replaceAll('_', ' ')}</option>
                            ))}
                          </GlassSelect>
                        </label>
                        <NumberField
                          label={text('Weight', 'الوزن')}
                          value={item.weight}
                          min={1}
                          max={100}
                          onChange={(weight) => updateTarget({
                            ...selectedTarget,
                            checklist: selectedTarget.checklist.map((current, index) =>
                              index === itemIndex ? { ...current, weight } : current),
                          })}
                        />
                        <GlassButton
                          type="button"
                          size="sm"
                          variant="danger"
                          className="self-end"
                          aria-label={text(`Remove check ${item.id}`, `إزالة الفحص ${item.id}`)}
                          onClick={() => updateTarget({
                            ...selectedTarget,
                            checklist: selectedTarget.checklist.filter((_, index) => index !== itemIndex),
                          })}
                        >
                          {text('Remove', 'إزالة')}
                        </GlassButton>
                      </div>
                      <div className="mt-2 grid gap-2 lg:grid-cols-2">
                        <label className="text-[10px] font-bold text-ink-faint">
                          {text('Label (English)', 'التسمية (بالإنجليزية)')}
                          <GlassInput
                            value={item.label.en}
                            dir="ltr"
                            onChange={(event) => updateTarget({
                              ...selectedTarget,
                              checklist: selectedTarget.checklist.map((current, index) =>
                                index === itemIndex
                                  ? { ...current, label: { ...current.label, en: event.target.value } }
                                  : current),
                            })}
                            className="mt-1"
                          />
                        </label>
                        <label className="text-[10px] font-bold text-ink-faint">
                          {text('Label (Arabic)', 'التسمية (بالعربية)')}
                          <GlassInput
                            value={item.label.ar}
                            dir="rtl"
                            onChange={(event) => updateTarget({
                              ...selectedTarget,
                              checklist: selectedTarget.checklist.map((current, index) =>
                                index === itemIndex
                                  ? { ...current, label: { ...current.label, ar: event.target.value } }
                                  : current),
                            })}
                            className="mt-1"
                          />
                        </label>
                        <label className="text-[10px] font-bold text-ink-faint lg:col-span-2">
                          {text('Evidence instruction', 'تعليمات الدليل')}
                          <GlassTextarea
                            value={item.instruction}
                            rows={2}
                            onChange={(event) => updateTarget({
                              ...selectedTarget,
                              checklist: selectedTarget.checklist.map((current, index) =>
                                index === itemIndex ? { ...current, instruction: event.target.value } : current),
                            })}
                            className="mt-1"
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="border-t border-aeblack-100 pt-5">
                <SectionHeader
                  title={text('Governed scenarios', 'السيناريوهات المحكومة')}
                  hint={text(
                    'Steps express intent; action toggles are the enforceable capability boundary.',
                    'تعبّر الخطوات عن الهدف؛ وتشكل مفاتيح الإجراءات حدود القدرات القابلة للتنفيذ.',
                  )}
                  action={(
                    <GlassButton
                      type="button"
                      size="sm"
                      onClick={() => updateTarget({
                        ...selectedTarget,
                        scenarios: [...selectedTarget.scenarios, {
                          id: nextId('scenario', selectedTarget.scenarios.map((scenario) => scenario.id)),
                          name: { en: '', ar: '' },
                          startPath: '/',
                          steps: ['Review the service page without submitting data.'],
                          allowedActions: ['navigate'],
                          fixture: {},
                        }],
                      })}
                    >
                      {text('Add scenario', 'إضافة سيناريو')}
                    </GlassButton>
                  )}
                />
                <div className="mt-3 space-y-3">
                  {selectedTarget.scenarios.map((scenario, scenarioIndex) => {
                    const dangerous = scenario.allowedActions.includes('sandbox_submit')
                      || scenario.allowedActions.includes('sandbox_payment');
                    const updateScenario = (next: typeof scenario) => updateTarget({
                      ...selectedTarget,
                      scenarios: selectedTarget.scenarios.map((current, index) =>
                        index === scenarioIndex ? next : current),
                    });
                    return (
                      <div key={`scenario-row-${scenarioIndex}`} className="rounded-xl border border-aeblack-100 bg-white p-4 shadow-sm">
                        <div className="grid gap-2 lg:grid-cols-[minmax(140px,.7fr)_1fr_1fr_auto]">
                          <label className="text-[10px] font-bold text-ink-faint">
                            {text('Scenario ID', 'معرف السيناريو')}
                            <GlassInput
                              value={scenario.id}
                              dir="ltr"
                              onChange={(event) => updateScenario({ ...scenario, id: event.target.value })}
                              className="mt-1 font-mono"
                            />
                          </label>
                          <label className="text-[10px] font-bold text-ink-faint">
                            {text('Name (English)', 'الاسم (بالإنجليزية)')}
                            <GlassInput
                              value={scenario.name.en}
                              dir="ltr"
                              onChange={(event) => updateScenario({
                                ...scenario,
                                name: { ...scenario.name, en: event.target.value },
                              })}
                              className="mt-1"
                            />
                          </label>
                          <label className="text-[10px] font-bold text-ink-faint">
                            {text('Name (Arabic)', 'الاسم (بالعربية)')}
                            <GlassInput
                              value={scenario.name.ar}
                              dir="rtl"
                              onChange={(event) => updateScenario({
                                ...scenario,
                                name: { ...scenario.name, ar: event.target.value },
                              })}
                              className="mt-1"
                            />
                          </label>
                          <GlassButton
                            type="button"
                            size="sm"
                            variant="danger"
                            className="self-end"
                            aria-label={text(`Remove scenario ${scenario.id}`, `إزالة السيناريو ${scenario.id}`)}
                            onClick={() => updateTarget({
                              ...selectedTarget,
                              scenarios: selectedTarget.scenarios.filter((_, index) => index !== scenarioIndex),
                            })}
                          >
                            {text('Remove', 'إزالة')}
                          </GlassButton>
                        </div>
                        <div className="mt-3 grid gap-3 lg:grid-cols-[.7fr_1.3fr]">
                          <label className="text-[10px] font-bold text-ink-faint">
                            {text('Start path', 'مسار البداية')}
                            <GlassInput
                              value={scenario.startPath}
                              dir="ltr"
                              onChange={(event) => updateScenario({ ...scenario, startPath: event.target.value })}
                              className="mt-1 font-mono"
                            />
                          </label>
                          <label className="text-[10px] font-bold text-ink-faint">
                            {text('Safe steps — one per line', 'الخطوات الآمنة — واحدة في كل سطر')}
                            <GlassTextarea
                              value={scenario.steps.join('\n')}
                              rows={4}
                              onChange={(event) => updateScenario({
                                ...scenario,
                                steps: event.target.value
                                  .split('\n'),
                              })}
                              className="mt-1"
                            />
                          </label>
                        </div>
                        <div className="mt-3">
                          <div className="mb-1 text-[10px] font-bold text-ink-faint">{text('Allowed actions', 'الإجراءات المسموح بها')}</div>
                          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
                            {ACTIONS.map((action) => (
                              <CheckboxField
                                key={action}
                                checked={scenario.allowedActions.includes(action)}
                                label={text(ACTION_LABELS[action].en, ACTION_LABELS[action].ar)}
                                tone={action === 'sandbox_submit' || action === 'sandbox_payment' ? 'warning' : 'default'}
                                onChange={(checked) => updateScenario({
                                  ...scenario,
                                  allowedActions: checked
                                    ? [...scenario.allowedActions, action]
                                    : scenario.allowedActions.filter((item) => item !== action),
                                })}
                              />
                            ))}
                          </div>
                        </div>
                        {dangerous && (
                          <div className="aegov-alert alert-warning mt-3" role="alert">
                            <div className="alert-content text-xs">
                              <strong>{text('Sandbox action selected.', 'تم تحديد إجراء تجريبي.')}</strong>{' '}
                              {text(
                                'Submission and payment may only run against an approved non-production fixture. Explicit confirmation below is required.',
                                'لا يجوز تنفيذ الإرسال والدفع إلا على بيانات اختبار غير إنتاجية معتمدة. يلزم التأكيد الصريح أدناه.',
                              )}
                            </div>
                          </div>
                        )}
                        <div className="mt-3 rounded-lg border border-aeblack-100 bg-aeblack-50 p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <div className="text-[10px] font-extrabold uppercase tracking-wider text-ink-faint">
                                {text('Fixture values', 'قيم بيانات الاختبار')}
                              </div>
                              <div className="text-[10px] text-ink-faint">
                                {text('Non-secret demo values only. Login data belongs in the managed credential reference.', 'قيم عرض غير سرية فقط. بيانات الدخول مكانها مرجع بيانات الاعتماد المُدار.')}
                              </div>
                            </div>
                            <GlassButton
                              type="button"
                              size="sm"
                              onClick={() => {
                                const entries = Object.entries(scenario.fixture);
                                let key = `fixture_${entries.length + 1}`;
                                while (key in scenario.fixture) key = `${key}_next`;
                                updateScenario({ ...scenario, fixture: { ...scenario.fixture, [key]: '' } });
                              }}
                            >
                              {text('Add fixture', 'إضافة بيانات اختبار')}
                            </GlassButton>
                          </div>
                          <div className="mt-2 space-y-2">
                            {Object.entries(scenario.fixture).map(([key, value], fixtureIndex) => (
                              <div key={fixtureIndex} className="grid gap-2 sm:grid-cols-[.8fr_1.2fr_auto]">
                                <GlassInput
                                  value={key}
                                  dir="ltr"
                                  aria-label={text(`Fixture key ${fixtureIndex + 1}`, `مفتاح بيانات الاختبار ${fixtureIndex + 1}`)}
                                  onChange={(event) => {
                                    const entries = Object.entries(scenario.fixture);
                                    entries[fixtureIndex] = [event.target.value, value];
                                    updateScenario({ ...scenario, fixture: Object.fromEntries(entries) });
                                  }}
                                  className="font-mono"
                                />
                                <GlassInput
                                  value={value}
                                  aria-label={text(`Fixture value ${fixtureIndex + 1}`, `قيمة بيانات الاختبار ${fixtureIndex + 1}`)}
                                  onChange={(event) => {
                                    const entries = Object.entries(scenario.fixture);
                                    entries[fixtureIndex] = [key, event.target.value];
                                    updateScenario({ ...scenario, fixture: Object.fromEntries(entries) });
                                  }}
                                />
                                <GlassButton
                                  type="button"
                                  size="sm"
                                  variant="danger"
                                  aria-label={text(`Remove fixture ${key}`, `إزالة بيانات الاختبار ${key}`)}
                                  onClick={() => updateScenario({
                                    ...scenario,
                                    fixture: Object.fromEntries(
                                      Object.entries(scenario.fixture).filter((_, index) => index !== fixtureIndex),
                                    ),
                                  })}
                                >
                                  {text('Remove', 'إزالة')}
                                </GlassButton>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {selectedTarget.scenarios.length === 0 && (
                    <div className="rounded-lg border border-dashed border-aeblack-200 p-4 text-center text-xs text-ink-faint">
                      {text('No scenarios configured. Checklist-only browsing remains available.', 'لا توجد سيناريوهات مهيأة. يظل التصفح وفق قائمة الفحص متاحاً.')}
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <CheckboxField
                    checked={selectedTarget.sandboxConfirmed}
                    label={text('I confirm this target is an approved demo or staging sandbox', 'أؤكد أن هذا الهدف بيئة عرض أو مرحلة تجريبية معتمدة')}
                    hint={text(
                      'Required only when any scenario can submit or perform a sandbox payment. This confirmation is explicit and versioned.',
                      'مطلوب فقط عندما يمكن لأي سيناريو الإرسال أو تنفيذ دفع تجريبي. هذا التأكيد صريح ومحفوظ بالإصدار.',
                    )}
                    tone="warning"
                    onChange={(sandboxConfirmed) => updateTarget({ ...selectedTarget, sandboxConfirmed })}
                  />
                </div>
              </section>
            </>
          ) : (
            <div className="grid min-h-64 place-items-center text-center">
              <div>
                <div className="text-4xl text-seablue-500" aria-hidden="true">◎</div>
                <h4 className="mt-3 text-sm font-extrabold">{text('Create a governed target', 'إنشاء هدف محكوم')}</h4>
                <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-ink-faint">
                  {text(
                    'A safe, read-only target seeds every service-quality dimension, a navigation-only scenario and recorded Browserbase defaults.',
                    'يبدأ هدف آمن للقراءة فقط بجميع أبعاد جودة الخدمة وسيناريو للتنقل فقط وإعدادات Browserbase المسجلة.',
                  )}
                </p>
                <GlassButton type="button" variant="primary" className="mt-4" onClick={addTarget}>
                  {text('Add first target', 'إضافة الهدف الأول')}
                </GlassButton>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-aeblack-100 bg-aeblack-50 p-5">
        <SectionHeader
          title={text('Browserbase + Stagehand binding', 'ربط Browserbase + Stagehand')}
          hint={text(
            'Recorded, logged browser sessions use one managed API-key reference. Raw API keys are never accepted by this editor.',
            'تستخدم جلسات المتصفح المسجلة ذات السجلات مرجعاً مُداراً واحداً لمفتاح API. لا يقبل هذا المحرر مفاتيح API الخام.',
          )}
        />
        <div className="mt-3 grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
          <CheckboxField
            checked={browserbase?.enabled ?? false}
            label={text('Enable Browserbase + Stagehand', 'تمكين Browserbase + Stagehand')}
            hint={text('Required when at least one target is configured.', 'مطلوب عند تهيئة هدف واحد على الأقل.')}
            onChange={(enabled) => updateBrowserbase((binding) => ({ ...binding, enabled }))}
          />
          <CheckboxField
            checked={booleanConfig(browserbaseConfig, 'recordSession', true)}
            label={text('Record session', 'تسجيل الجلسة')}
            hint={text('Retain visual replay evidence.', 'الاحتفاظ بدليل الإعادة المرئي.')}
            onChange={(recordSession) => updateBrowserbase((binding) => ({
              ...binding,
              config: { ...binding.config, recordSession },
            }))}
          />
          <CheckboxField
            checked={booleanConfig(browserbaseConfig, 'logSession', true)}
            label={text('Log session', 'تدوين سجل الجلسة')}
            hint={text('Capture governed browser diagnostics.', 'التقاط تشخيصات المتصفح المحكومة.')}
            onChange={(logSession) => updateBrowserbase((binding) => ({
              ...binding,
              config: { ...binding.config, logSession },
            }))}
          />
          <label className="block text-xs font-bold text-ink-soft">
            {text('Region', 'المنطقة')}
            <GlassSelect
              value={typeof browserbaseConfig.region === 'string' ? browserbaseConfig.region : 'eu-central-1'}
              onChange={(event) => updateBrowserbase((binding) => ({
                ...binding,
                config: { ...binding.config, region: event.target.value },
              }))}
              className="mt-1"
              dir="ltr"
            >
              {['eu-central-1', 'us-east-1', 'us-west-2', 'ap-southeast-1'].map((region) => (
                <option key={region} value={region}>{region}</option>
              ))}
            </GlassSelect>
          </label>
          <div className="lg:col-span-2">
            <ManagedReferenceField
              label={text('Managed Browserbase API-key reference', 'مرجع مفتاح Browserbase API المُدار')}
              value={browserbase?.secretRef ?? null}
              placeholder="env://BROWSERBASE_API_KEY"
              onChange={(secretRef) => updateBrowserbase((binding) => ({ ...binding, secretRef }))}
              help={text(
                'Reference only: env://. The default is env://BROWSERBASE_API_KEY.',
                'مرجع فقط: env://. الافتراضي هو env://BROWSERBASE_API_KEY.',
              )}
            />
          </div>
          <NumberField
            label={text('Maximum provider concurrency', 'الحد الأقصى لتزامن المزوّد')}
            value={numberConfig(browserbaseConfig, 'maxConcurrency', 1)}
            min={1}
            max={20}
            onChange={(maxConcurrency) => updateBrowserbase((binding) => ({
              ...binding,
              config: { ...binding.config, maxConcurrency },
            }))}
          />
          <NumberField
            label={text('Session timeout (seconds)', 'مهلة الجلسة (ثوانٍ)')}
            value={numberConfig(browserbaseConfig, 'sessionTimeoutSeconds', 1_800)}
            min={60}
            max={21_600}
            step={30}
            onChange={(sessionTimeoutSeconds) => updateBrowserbase((binding) => ({
              ...binding,
              config: { ...binding.config, sessionTimeoutSeconds },
            }))}
          />
        </div>
      </div>

      {issues.length > 0 && (
        <div className="border-t border-aegold-300 bg-aegold-50 p-4" role="status">
          <div className="text-xs font-extrabold text-gold-deep">
            {text('Complete these checks before saving', 'أكمل هذه المتطلبات قبل الحفظ')}
          </div>
          <ul className="mt-2 list-disc space-y-1 ps-5 text-[11px] leading-4 text-ink-soft">
            {issues.slice(0, 8).map((issue) => (
              <li key={issue}>{lang === 'ar' ? arabicIssue(issue) : issue}</li>
            ))}
          </ul>
          {issues.length > 8 && (
            <p className="mt-2 text-[10px] text-ink-faint">
              {text(`And ${issues.length - 8} more checks.`, `و${issues.length - 8} متطلبات إضافية.`)}
            </p>
          )}
        </div>
      )}
    </GlassCard>
  );
}
